import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, ShieldAlert, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * AdminRoute - guards admin-only routes.
 * - While auth is loading: spinner
 * - Not signed in: shows inline admin sign-in form (so users can log in directly)
 * - Signed in but not admin: redirect to home + toast
 * - Signed in admin: renders children
 */
export default function AdminRoute({ children }) {
  var auth = useAuth();
  var navigate = useNavigate();
  var [email, setEmail] = useState("");
  var [password, setPassword] = useState("");
  var [submitting, setSubmitting] = useState(false);

  function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    auth.login(email, password)
      .then(function(res) {
        if (res && res.user && res.user.is_admin) {
          toast.success("Welcome, admin");
        } else {
          toast.error("This account is not an admin");
          navigate("/", { replace: true });
        }
      })
      .catch(function(err) {
        var msg = (err && err.response && err.response.data && err.response.data.detail) || "Invalid credentials";
        toast.error(msg);
      })
      .finally(function() { setSubmitting(false); });
  }

  if (auth.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]" data-testid="admin-route-loading">
        <Loader2 className="w-8 h-8 text-[#C0A062] animate-spin" />
      </div>
    );
  }

  // Signed in but not admin — kick them out
  if (auth.isAuthenticated && !auth.user?.is_admin) {
    toast.error("Not authorized — admin access required");
    return <Navigate to="/" replace />;
  }

  // Signed in admin — render the admin page
  if (auth.isAuthenticated && auth.user?.is_admin) {
    return children;
  }

  // Not signed in — show admin sign-in form inline
  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#0a0a0a]" data-testid="admin-signin">
      <div className="container-tactical">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#C0A062]/10 border border-[#C0A062]/30 flex items-center justify-center">
              <ShieldAlert className="w-7 h-7 text-[#C0A062]" />
            </div>
            <p className="font-mono text-[#C0A062] text-sm uppercase tracking-[0.3em] mb-2">Restricted Access</p>
            <h1 className="font-heading font-black text-3xl text-white uppercase">Admin Sign In</h1>
            <p className="text-[#A1A1AA] text-sm mt-2">This area is for store administrators only.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 p-6 bg-[#121212] border border-white/10 rounded-sm">
            <div className="space-y-2">
              <Label className="text-[#A1A1AA] text-xs uppercase">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={function(e) { setEmail(e.target.value); }}
                required
                autoFocus
                className="input-tactical"
                placeholder="admin@resurrectedgunparts.com"
                data-testid="admin-signin-email"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#A1A1AA] text-xs uppercase">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={function(e) { setPassword(e.target.value); }}
                required
                className="input-tactical"
                placeholder="••••••••••"
                data-testid="admin-signin-password"
              />
            </div>
            <Button type="submit" className="btn-primary w-full" disabled={submitting} data-testid="admin-signin-submit">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LogIn className="w-4 h-4 mr-2" />}
              {submitting ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
